import React, { Dispatch, useCallback, useEffect, useMemo } from 'react';
import { mdiChevronDown, mdiChevronRight } from '@mdi/js';
import { SxProps } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { IDocDefaultFields } from '../../../redux/project/documents/sliceProjectDocuments';
import { IAppReduxStore } from '../../../redux/store';
import { actionColumnWidth, defaultBorder } from '../../../Theme';
import { getAdditionalDocs } from '../../../redux/project/documents/sliceProjectAdditionalDocuments';
import { CustomTreeLeaf } from './CustomTreeLeaf';
import { SimpleTreeView, TreeItem, TreeItemProps } from '@mui/x-tree-view';
import IconWrapper from '../Icons/IconWrapper';

/** TreeView Styles. */
const treeViewSx: SxProps = {
    height: '100%',
    overflowY: 'auto',
    '& .MuiTreeItem-group': {
        ml: 3.5,
    },
    '& .MuiTreeItem-content': {
        borderBottom: defaultBorder,
        padding: '0px 8px',
    },
};

/**
 * TreeView that shows information on the additional documents associated with a given document.
 */
export default ({ universalId, noteId, small, disableClick }: IAdditionalDocsTreeViewProps) => {
    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get the replicaId from the react router. */
    const { replicaId } = useParams();

    /** Get relevant values from Redux store. */
    const allAdditionalDocs = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.documents.data,
    );
    const fields = useSelector((store: IAppReduxStore) => store.projectAdditionalDocs.fields.data);
    const fieldSchema = useSelector(
        (store: IAppReduxStore) => store.projectAdditionalDocs.schema.data,
    );

    /** Additional documents for the main document (by NoteId). */
    const additionalDocs = useMemo(() => {
        return allAdditionalDocs?.[noteId];
    }, [allAdditionalDocs, noteId]);

    /** Checks if at least one document has children. */
    const hasChildren = Boolean(additionalDocs?.some(doc => doc.ChildCount > 0));

    /** Effect that fetches the additional documents for this document, if they have not already been requested. */
    useEffect(() => {
        !additionalDocs && fetchAdditionalDocuments(universalId, noteId);
    }, [additionalDocs, universalId, noteId]);

    /**
     * Gets the properties for a TreeItem.
     * @param id -> TreeItem's NoteId.
     */
    const getTreeItemProps = useCallback(
        (id: string): TreeItemProps => ({ sx: { alignItems: 'center' }, itemId: id }),
        [],
    );

    /**
     * Creates the child nodes for a given document.
     * @param doc - Target document.
     */
    const getChildNodes = useCallback(
        (doc: IDocDefaultFields) =>
            !allAdditionalDocs[doc.NoteId]
                ? new Array(doc.ChildCount)
                      .fill(null)
                      .map((_, i) => `${doc.NoteId}_${i}`)
                      .map(x => <TreeItem key={x} {...getTreeItemProps(x)} />)
                : mapNode(doc.NoteId),
        [allAdditionalDocs],
    );

    /**
     * Creates a TreeItem for a given document.
     * @param id -> Target document's NoteId.
     */
    const mapNode = useCallback(
        (id: string) =>
            allAdditionalDocs[id] ? (
                allAdditionalDocs[id].map(doc => (
                    <TreeItem
                        key={doc.NoteId}
                        ContentComponent={CustomTreeLeaf({
                            doc,
                            mainDoc: universalId,
                            disableClick,
                            small,
                            hasChildren,
                        })}
                        {...getTreeItemProps(doc.NoteId)}
                    >
                        {doc.ChildCount > 0 && getChildNodes(doc)}
                    </TreeItem>
                ))
            ) : (
                <></>
            ),
        [allAdditionalDocs, small],
    );

    /**
     * Searches for the idDocIndex that is associated with a given NoteId.
     * @param id -> Target Noteid.
     * @returns Matching idDocIndex, or null if nothing was found.
     */
    const findIdDocIndex = useCallback(
        (id: string): string =>
            Object.keys(allAdditionalDocs)
                .map(x => allAdditionalDocs[x].filter(y => y.NoteId === id)[0]?.IdDocIndex)
                .filter(Boolean)[0],
        [allAdditionalDocs],
    );

    /**
     * Fetches the additional documents for a given document.
     * @param idDoc -> Target document's IdDocIndex.
     * @param idNote -> Target document's NoteId.
     */
    const fetchAdditionalDocuments = useCallback(
        (idDoc: string, idNote: string) =>
            idDoc &&
            idNote &&
            dispatch(
                getAdditionalDocs({
                    replicaId,
                    universalId: idDoc,
                    noteId: idNote,
                    fields: [].concat(
                        ...fieldSchema.map(
                            uf => fields.filter(x => x.field === uf)[0]?.fields ?? [],
                        ),
                    ),
                }),
            ),
        [replicaId, fields, fieldSchema],
    );

    /**
     * Fetches the additional documents for open nodes in the TreeView.
     * @param itemIds -> Array of open TreeItems in the TreeView.
     */
    const handleChange = useCallback(
        (itemIds: string[]) => {
            itemIds.forEach(
                id => !allAdditionalDocs[id] && fetchAdditionalDocuments(findIdDocIndex(id), id),
            );
        },
        [allAdditionalDocs],
    );

    return (
        <SimpleTreeView
            sx={{ ...treeViewSx, marginLeft: !small ? actionColumnWidth / 8 : 'unset' }}
            slots={{
                expandIcon: IconWrapper({ path: mdiChevronRight, size: '0.8', withWrap: true }),
                collapseIcon: IconWrapper({ path: mdiChevronDown, size: '0.8', withWrap: true }),
            }}
            onExpandedItemsChange={(e, itemIds) => handleChange(itemIds)}
            children={mapNode(noteId)}
        />
    );
};

/**
 * Interface that describes the props for an AdditionalDocsTreeView component.
 */
export interface IAdditionalDocsTreeViewProps {
    /** UniversalId of the document whose additional documents are being shown. */
    universalId: string;

    /** NoteId of the document whose additional documents are being shown. */
    noteId: string;

    /** Optional flag that manipulates the size of the TreeView's items. */
    small?: boolean;

    /** (OPTIONAL) Flag that prevents on-click events for DocTypeIcon being triggered. */
    disableClick?: boolean;
}
